import { AxiosResponse, AxiosError } from 'axios';
import Api from '@sendsprint/api-types';
import { getEnvData } from '@src/utils/env';
import { getState } from '@src/utils/stateStorage';
import { AdminAxiosConfig, BusinessAxiosConfig, UserAxiosConfig } from '@src/config/AxiosConfig';
import { AddToast, ToastType } from '@src/contexts';
import { AuthContextI } from '@src/contexts/auth-context';
import ClientApi from '@src/types/client';
export type { AxiosRequestConfig as RequestConfig } from 'axios';

const ENV_DATA = getEnvData();

type ConfigureInterceptorProps = {
  addToast: AddToast;
  logout: AuthContextI['logout'];
  handleLogoutSuccess: AuthContextI['handleLogoutSuccess'];
};

export function configureInterceptors({
  addToast,
  logout,
  handleLogoutSuccess
}: ConfigureInterceptorProps) {
  const addApiErrorToast = (content: ToastType['content']) => {
    addToast(content, { appearance: 'warning' });
  };

  const responseInterceptor = UserAxiosConfig.interceptors.response.use(
    function (response: AxiosResponse<Api.Endpoint.Response.SuccessResponse>) {
      return response;
    },
    function (
      error: AxiosError<
        Api.Endpoint.Response.ErrorResponse | Api.Endpoint.Response.InternalServerErrorResponse
      >
    ) {
      const errorResponse = error.response?.data as Api.Endpoint.Response.ErrorResponse | undefined;
      const serverErrorResponse = error.response?.data as
        | Api.Endpoint.Response.InternalServerErrorResponse
        | undefined;

      const getReturnObject = () => Promise.reject(error.response?.data || error);

      if (errorResponse?.ResponseCode === Api.Endpoint.Response.Code.AuthenticationFailed) {
        const errMessage = errorResponse?.ResponseMessage;
        const isAuthenticationFailedMessage =
          errMessage === 'Authentication Failed. Please login again.';

        if (isAuthenticationFailedMessage) {
          logout(true)
            .then(() => {
              addApiErrorToast({
                title: 'Authentication Failed',
                body: errMessage
              });
            })
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            .catch(() => {});
        }
      }

      // Invalid or expired hash, then log out
      if (errorResponse?.ResponseCode === Api.Endpoint.Response.Code.InvalidHash) {
        // this is for error handling of register form
        // eslint-disable-next-line no-empty
        if (errorResponse?.ResponseMessage === 'Email already registered before. Please login') {
          addApiErrorToast({
            title: 'Error',
            body: 'Email already registered. Please Login or reset password.'
          });
        } else {
          logout(true)
            .then(() => {
              addApiErrorToast({
                title: 'Your session has expired',
                body: 'Please log in to your account'
              });
            })
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            .catch(() => {});
        }
        return getReturnObject();
      }

      if (error?.config?.showToastOnError) {
        const errorMessage: string =
          errorResponse?.ResponseMessage || serverErrorResponse?.Message || error.message;

        addApiErrorToast({ title: 'Error', body: errorMessage });
      }

      return getReturnObject();
    }
  );

  const businessResponseInterceptor = BusinessAxiosConfig.interceptors.response.use(
    function (response: AxiosResponse<Api.Endpoint.Response.SuccessResponse>) {
      return response;
    },
    function (
      error: AxiosError<
        Api.Endpoint.Response.ErrorResponse | Api.Endpoint.Response.InternalServerErrorResponse
      >
    ) {
      // const errorResponse = error.response?.data as Api.Endpoint.Response.ErrorResponse | undefined;
      // const serverErrorResponse = error.response?.data as
      //   | Api.Endpoint.Response.InternalServerErrorResponse
      //   | undefined;

      const errorResponse = error.response?.data as ClientApi.Response.ResponseBody | undefined;
      // const serverErrorResponse = error.response?.data as
      //   | Api.Endpoint.Response.InternalServerErrorResponse
      //   | undefined;

      const getReturnObject = () => Promise.reject(error.response?.data || error);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const _errResponse = errorResponse as any;

      const isUnauthenticated =
        _errResponse === 'Authentication Failed' ||
        _errResponse?.title === 'Unauthorized' ||
        _errResponse?.responseMessage?.title === 'Invalid session token';
      if (isUnauthenticated) {
        handleLogoutSuccess();
        addApiErrorToast({
          title: 'Your session has expired',
          body: 'Please log in to your account'
        });
      }

      // Invalid or expired hash, then log out
      if (errorResponse?.httpStatusCode === 401) {
        const errMessage = errorResponse?.responseMessage?.title;
        // this is for error handling of register form
        if (errMessage === 'Authentication Failed') {
          handleLogoutSuccess();
          addApiErrorToast({
            title: 'Your session has expired',
            body: 'Please log in to your account'
          });
        }

        if (errMessage === 'Your profile is deactivated. Please contact an admin') {
          handleLogoutSuccess();
          addApiErrorToast({
            title: 'Your session has expired',
            body: errMessage
          });
        }
        return getReturnObject();
      }

      if (error?.config?.showToastOnError) {
        const errorMessage: string =
          errorResponse?.responseMessage?.title ||
          errorResponse?.responseMessage?.actionMessage ||
          error?.message;

        addApiErrorToast({ title: 'Error', body: errorMessage });
      }

      return getReturnObject();
    }
  );

  const businessRequestInterceptor = BusinessAxiosConfig.interceptors.request.use(
    (request: any) => {
      // Get the token from local storage
      const userState = getState();
      const envData = getEnvData();
      const token = userState?.tokens?.token?.accessToken;

      request.headers.ApplicationID = envData.API_APPLICATION_ID;
      request.headers.ApplicationSecret = envData.API_SECRET_KEY;

      // If the token exists, set the Authorization header
      if (token) {
        request.headers.Authorization = `Bearer ${token}`;
        request.headers.SessionToken = ` ${token}`;
      }

      return request;
    }
  );

  const adminResponseInterceptor = AdminAxiosConfig.interceptors.response.use(
    function (response: AxiosResponse<Api.Endpoint.Response.SuccessResponse>) {
      return response;
    },
    function (
      error: AxiosError<
        Api.Endpoint.Response.ErrorResponse | Api.Endpoint.Response.InternalServerErrorResponse
      >
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorResponse = error.response?.data as Api.Endpoint.Response.ErrorResponse | any;
      const serverErrorResponse = error.response?.data as
        | Api.Endpoint.Response.InternalServerErrorResponse
        | undefined;
      const errorStatus = error.response?.status;

      const getReturnObject = () => Promise.reject(error.response?.data || error);

      // Invalid or expired hash, then log out
      if (
        // errorResponse?.ResponseCode === Api.Endpoint.Response.Code.InvalidHash ||
        errorResponse?.httpStatusCode === 401
      ) {
        // this is for error handling of register form
        if (errorResponse?.ResponseMessage === 'Email already registered before. Please login') {
          logout();
          addApiErrorToast({
            title: 'Your session has expired',
            body: 'Please log in to your account'
          });
        } else {
          logout();
          addApiErrorToast({
            title: 'Your session has expired',
            body: 'Please log in to your account'
          });
        }
        return getReturnObject();
      }

      if (
        errorStatus === 401 ||
        errorResponse?.ResponseMessage === 'Invalid session token.' ||
        errorResponse?.ResponseMessage === 'Session token has expired.'
      ) {
        logout();
        addApiErrorToast({
          title: 'Your session has expired',
          body: 'Please log in to your account'
        });
      }

      if (error?.config?.showToastOnError) {
        const errorMessage: string =
          errorResponse?.ResponseMessage || serverErrorResponse?.Message || error.message;

        addApiErrorToast({ title: 'Error', body: errorMessage });
      }

      return getReturnObject();
    }
  );

  const adminRequestInterceptor = AdminAxiosConfig.interceptors.request.use((request: any) => {
    // Get the token from local storage
    const userState = getState();
    const envData = getEnvData();
    const token = userState?.tokens?.adminRefreshToken?.sessionToken;

    request.headers.ApplicationID = envData.ADMIN_API_APPLICATION_ID;
    request.headers.ApplicationSecret = envData.ADMIN_API_SECRET_KEY;

    // If the token exists, set the Authorization header
    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
      request.headers.SessionToken = ` ${token}`;
    }

    return request;
  });

  return function cleanupInterceptors(): void {
    UserAxiosConfig.interceptors.response.eject(responseInterceptor);
    BusinessAxiosConfig.interceptors.response.eject(businessResponseInterceptor);
    BusinessAxiosConfig.interceptors.request.eject(businessRequestInterceptor);
    AdminAxiosConfig.interceptors.response.eject(adminResponseInterceptor);
    AdminAxiosConfig.interceptors.request.eject(adminRequestInterceptor);
  };
}

export function decorateWithAppIdAndKey<T>(data: T): T & Api.Endpoint.Request.DataWithApiIdAndKey {
  return {
    ...data,
    ApplicationID: ENV_DATA.API_APPLICATION_ID,
    SecretKey: ENV_DATA.API_SECRET_KEY
  };
}

export function decorateWithEmailAndHash<T>(
  data: T
): T & Api.Endpoint.Request.DataWithEmailAndHash {
  const state = getState();
  const hash = (state && state.tokens && state.tokens.hash) || '';
  const email = (state && state.user && state.user.email) || '';

  return {
    Email: email,
    Hash: hash,
    ...data
  };
}

export function decorateWithAppIdAndKeyAndEmailAndHash<T>(
  data: T
): T & Api.Endpoint.Request.DataWithApiAndKeyAndEmailAndHash {
  return decorateWithAppIdAndKey(decorateWithEmailAndHash(data));
}

/**
 * Response extractor. Get the actual data/info returned from an API endpoint
 * @param response
 */
export function getApiEndpointResponse<T>(response: AxiosResponse<T>): T {
  return response.data;
}
